// Every image an author can put into a post, from the moment they pick a file to
// the payload that gets committed: naming, measuring, and the checks that catch
// a file that will look wrong on the published page while it can still be swapped.

import type { DraftImage } from '@/services/blogApi'

/** Width of the post body column on the published page (`max-w-184`). */
export const CONTENT_WIDTH_PX = 736

/**
 * Covers render at roughly 648 CSS px in the post header (a 76rem frame split
 * 0.85/1.15) and fill the card on the index, so this is the width at which one
 * is still sharp on a 2× screen. A soft cover is the first thing a reader sees.
 */
export const COVER_WIDTH_PX = 1280

/** `aspect-video`, cropped with `object-cover` — anything far off this loses edges. */
export const COVER_ASPECT = 16 / 9

/** Matches the server's publish-time filename check, so a bad name is caught here. */
const SAFE_FILENAME = /^[a-z0-9][a-z0-9.-]*\.(png|jpg|jpeg|gif|webp|svg|avif)$/i

const EXTENSIONS: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
  'image/avif': 'avif',
}

export const MAX_IMAGE_BYTES = 8 * 1024 * 1024

/** What a file picker, a drop zone and a paste should all accept. */
export const ACCEPT_IMAGES = Object.keys(EXTENSIONS).join(',')

export class ImageReplaceError extends Error {}

function readAsDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new ImageReplaceError('Could not read that file'))
    reader.readAsDataURL(file)
  })
}

/**
 * Natural pixel size, from the browser rather than a header parser — it already
 * has a decoder, and this runs on an image the author just picked.
 */
function measure(dataUri: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight })
    image.onerror = () => reject(new ImageReplaceError('That file is not a readable image'))
    image.src = dataUri
  })
}

function extensionOf(file: File): string {
  const ext = EXTENSIONS[file.type.toLowerCase()]
  if (!ext) {
    throw new ImageReplaceError(
      `${file.type || 'That file type'} is not supported. Use PNG, JPEG, GIF, WebP, SVG or AVIF.`
    )
  }
  return ext
}

/**
 * Read, measure and name one picked file. `base` is the filename without an
 * extension; the extension always comes from the file itself, so swapping a PNG
 * for a JPEG never produces a JPEG lying about being a PNG.
 */
async function build(file: File, base: string, sharpAt: number): Promise<DraftImage> {
  if (file.size > MAX_IMAGE_BYTES) {
    throw new ImageReplaceError(
      `That image is ${(file.size / 1024 / 1024).toFixed(1)}MB. The limit is ${MAX_IMAGE_BYTES / 1024 / 1024}MB.`
    )
  }

  const ext = extensionOf(file)
  const dataUri = await readAsDataUri(file)
  // SVG has no meaningful pixel size; treat it as always sharp.
  const size = ext === 'svg' ? { width: sharpAt, height: 0 } : await measure(dataUri)

  const filename = `${base}.${ext}`
  if (!SAFE_FILENAME.test(filename)) {
    throw new ImageReplaceError(`"${filename}" is not a usable filename.`)
  }

  const base64 = dataUri.replace(/^data:[^;]+;base64,/, '')

  return {
    filename,
    url: `/blog-images/${filename}`,
    dataUri,
    bytes: Math.round((base64.length * 3) / 4),
    width: size.width,
    height: size.height,
    lowRes: ext !== 'svg' && size.width < sharpAt,
  }
}

/**
 * Turns a picked file into a `DraftImage` that replaces `existingUrl`.
 *
 * The filename keeps the old base but takes the new file's extension, so the
 * caller must rewrite the body reference when `url` changes —
 * `replacementNeedsBodyRewrite` says when.
 */
export function buildReplacement(file: File, existingUrl: string): Promise<DraftImage> {
  const oldName = existingUrl.split('/').pop() ?? 'image.png'
  return build(file, oldName.replace(/\.[^.]+$/, ''), CONTENT_WIDTH_PX)
}

/**
 * Filename stem for the next body image: the post slug and the lowest index not
 * already taken, so the repo stays legible and re-uploading after a delete
 * reuses the freed number rather than counting forever.
 *
 * Matching is extension-insensitive — `post-1.png` must also rule out
 * `post-1.jpg`, since committing both would put two different files in what the
 * author thinks of as one slot.
 */
export function nextImageBase(slug: string, taken: Iterable<string>): string {
  const stems = new Set([...taken].map(name => name.replace(/\.[^.]+$/, '')))
  const stem = slug || 'image'
  let index = 1
  while (stems.has(`${stem}-${index}`)) index += 1
  return `${stem}-${index}`
}

/** A newly uploaded body image, named by `nextImageBase`. */
export function buildUpload(
  file: File,
  slug: string,
  taken: Iterable<string>
): Promise<DraftImage> {
  return build(file, nextImageBase(slug, taken), CONTENT_WIDTH_PX)
}

/**
 * Cover art. One per post and named for it, so re-uploading replaces the file
 * in place instead of leaving the old one behind.
 */
export function buildCover(file: File, slug: string): Promise<DraftImage> {
  return build(file, `${slug || 'post'}-cover`, COVER_WIDTH_PX)
}

/** True when the swap changed the file extension, so the body must be updated. */
export function replacementNeedsBodyRewrite(oldUrl: string, next: DraftImage): boolean {
  return oldUrl !== next.url
}

/** `dashboard-v2_final.png` -> `Dashboard v2 final`, a starting point for alt text. */
export function altFromFilename(name: string): string {
  const stem = name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim()
  return stem ? stem.charAt(0).toUpperCase() + stem.slice(1) : 'Image'
}

/** Caption shown under an image in the preview. */
export function describe(image: DraftImage | undefined, pending: boolean): string {
  if (!image) return ''
  const size = image.width ? `${image.width}×${image.height}` : 'unknown size'
  const kb = `${Math.round(image.bytes / 1024)}KB`
  const where = pending ? `uploads as ${image.filename} when you publish` : image.filename
  const soft = image.lowRes
    ? ` · narrower than the ${CONTENT_WIDTH_PX}px article column, so it will look soft — replace it with a wider version`
    : ''
  return `${where} · ${size} · ${kb}${soft}`
}

/**
 * A Markdown image reference. `![alt](url)` — the title form `![alt](url "t")`
 * is matched too, since the parser accepts it, but nothing here writes one.
 */
const IMAGE_REF = /!\[([^\]]*)\]\(\s*([^()\s]+)(?:\s+"[^"]*")?\s*\)/g

export interface BodyImage {
  /** Position in document order; the handle for editing this one reference. */
  index: number
  alt: string
  url: string
  /** Character range of the alt text within the body. */
  altStart: number
  altEnd: number
}

/**
 * Every image the body renders, in the order a reader meets them.
 *
 * Fenced code is skipped: an image reference inside a ``` block is sample text,
 * not an image, and rewriting it would corrupt the sample.
 */
export function bodyImages(body: string): BodyImage[] {
  const found: BodyImage[] = []
  let offset = 0

  body.split(/(```[\s\S]*?```)/g).forEach((part, partIndex) => {
    if (partIndex % 2 === 0) {
      for (const match of part.matchAll(IMAGE_REF)) {
        const altStart = offset + (match.index ?? 0) + 2
        found.push({
          index: found.length,
          alt: match[1],
          url: match[2],
          altStart,
          altEnd: altStart + match[1].length,
        })
      }
    }
    offset += part.length
  })

  return found
}

/**
 * Alt text as Markdown can carry it: `]` would close the reference early, and a
 * newline would end the paragraph, so neither can survive.
 */
export function sanitizeAlt(text: string): string {
  return text.replace(/[[\]]/g, '').replace(/[\r\n]+/g, ' ')
}

/** Rewrite one image's alt text, leaving every other reference to the same file alone. */
export function setImageAlt(body: string, index: number, alt: string): string {
  const image = bodyImages(body)[index]
  if (!image) return body
  const clean = sanitizeAlt(alt)
  return body.slice(0, image.altStart) + clean + body.slice(image.altEnd)
}

/** Every `/blog-images/...` path the body currently references. */
export function referencedImages(body: string): Set<string> {
  const found = new Set<string>()
  for (const match of body.matchAll(/\/blog-images\/[A-Za-z0-9._-]+/g)) found.add(match[0])
  return found
}

/**
 * Image files out of a drop or a paste. Both carry a `DataTransfer`, so one
 * function serves both; a paste of ordinary text has no files and yields none,
 * which is what lets the caller fall through to its normal paste handling.
 */
export function imageFilesFrom(transfer: DataTransfer | null | undefined): File[] {
  return [...(transfer?.files ?? [])].filter(file => file.type.startsWith('image/'))
}

/**
 * Whether a drag is carrying files.
 *
 * `dragover` cannot see the files themselves — the spec only exposes them once
 * the drop happens — so a handler that decides by inspecting `files` would never
 * call `preventDefault`, and the browser would then refuse to fire `drop` at all.
 * The types list is readable throughout the drag, which is what makes it usable
 * as the "is this a file drop" test.
 */
export function isFileDrag(transfer: DataTransfer | null | undefined): boolean {
  return [...(transfer?.types ?? [])].includes('Files')
}

/**
 * Splice a Markdown image into the body at `caret`, on its own block.
 *
 * Returns the new body and the range covering the alt text, so the editor can
 * select it — the author lands on the one part of the snippet worth editing.
 */
export function insertImageMarkdown(
  body: string,
  caret: number,
  image: DraftImage,
  alt: string
): { body: string; altStart: number; altEnd: number; end: number } {
  const at = Math.max(0, Math.min(caret, body.length))
  const before = body.slice(0, at)
  const after = body.slice(at)

  // A Markdown image only becomes a block if a blank line separates it from the
  // surrounding prose; inline, it would render inside the previous paragraph.
  const lead = before === '' ? '' : before.endsWith('\n\n') ? '' : before.endsWith('\n') ? '\n' : '\n\n'
  const trail = after === '' ? '\n' : after.startsWith('\n\n') ? '' : after.startsWith('\n') ? '\n' : '\n\n'

  const snippet = `![${alt}](${image.url})`
  const altStart = before.length + lead.length + 2
  return {
    body: `${before}${lead}${snippet}${trail}${after}`,
    altStart,
    altEnd: altStart + alt.length,
    /** Just past the inserted block, so a second image lands after the first. */
    end: before.length + lead.length + snippet.length + trail.length,
  }
}

/**
 * Where in a textarea's text a drop at these viewport coordinates landed.
 *
 * A file drop does not move the caret the way a text drop does, so without this
 * an image would always land wherever the caret happened to be last. Both APIs
 * resolve into the textarea's internal text node; the caller has already
 * established the point is inside this textarea, so the offset only has to be
 * clamped, and falls back to the caret when neither API is available.
 */
export function caretFromPoint(textarea: HTMLTextAreaElement, x: number, y: number): number {
  const doc = document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offset: number } | null
    caretRangeFromPoint?: (x: number, y: number) => Range | null
  }
  const offset =
    doc.caretPositionFromPoint?.(x, y)?.offset ??
    doc.caretRangeFromPoint?.(x, y)?.startOffset ??
    textarea.selectionStart

  return Math.max(0, Math.min(offset ?? textarea.value.length, textarea.value.length))
}
